<template>
  <div class="profile-edit-container">
    <div class="registration-feedback feedback">
      <transition name="fade" @enter="enterError">
        <FeedbackError v-show="hasErrors" :message="feedbackErrorMessage" />
      </transition>
      <transition name="fade" @enter="enterFeedbackSuccess">
        <FeedbackSuccess
          v-show="hasFeedbackSuccess"
          :message="feedbackSuccessMessage"
        />
      </transition>
    </div>
    <section class="profile-edit-section">
      <div v-if="!isApp" class="profile-edit-section__head">
        <PageHeadline title="プロフィール設定" />
      </div>
      <div class="profile-edit-section__body">
        <div class="profile-edit-form">
          <form
            :action="postUrl"
            method="POST"
            accept-charset="UTF-8"
            name="profile_edit_form"
            enctype="multipart/form-data"
          >
            <input name="utf8" type="hidden" value="✓" />
            <input
              type="hidden"
              name="authenticity_token"
              :value="authenticityToken"
            />
            <div class="profile-edit-section__main">
              <div class="profile-edit-section__main-content">
                <div class="profile-edit-form__icon-area">
                  <div class="profile-edit-form__icon-field">
                    <label for="file_upload">
                      <AccountIconWh104
                        v-if="!isUploaded"
                        class="profile-edit-form__account-icon"
                        :image-url="currentImage"
                      />
                      <AccountIconWh104
                        v-else
                        :image-url="uploadedImage"
                        class="profile-edit-form__account-icon"
                      />
                      <div class="profile-edit-form__icon-overlay">
                        <CameraBlack />
                      </div>
                    </label>
                    <input
                      id="file_upload"
                      name="profile_edit_form[image]"
                      type="file"
                      accept="image/*"
                      @change="onFileChange"
                    />
                    <input
                      id="profile_edit_form_temp_image_file"
                      v-model="tempImageFile"
                      name="profile_edit_form[temp_image_file]"
                      type="hidden"
                    />
                    <input
                      id="profile_edit_form_image_id"
                      v-model="imageId"
                      name="profile_edit_form[image_id]"
                      type="hidden"
                    />
                    <button
                      v-if="currentImage || uploadedImage"
                      class="profile-edit-form__icon-delete-button"
                      @click.prevent="onClickIconDelete"
                    >
                      画像を削除
                    </button>
                  </div>
                  <div
                    v-if="hasFileError"
                    class="profile-edit-form__alert-box-area box-list--alert"
                  >
                    <ul>
                      <li>{{ fileErrorMessage }}</li>
                    </ul>
                  </div>
                </div>
                <a
                  class="profile-edit-form__profile-guide-link"
                  href="https://giftee.com/announcements/472"
                >
                  プロフィール活用方法を見る
                </a>
                <div
                  v-if="errorMessages.length >= 1"
                  class="profile-edit-form__alert-box-area box-list--alert"
                >
                  <ul>
                    <li
                      v-for="(errorMessage, index) in errorMessages"
                      :key="index"
                      class="error_message"
                    >
                      {{ errorMessage }}
                    </li>
                  </ul>
                </div>
                <div class="profile-edit-form__input-content-area">
                  <div
                    class="profile-edit-form__nickname profile-edit-form__group"
                  >
                    <div
                      class="profile-edit-form__required_caution minimal require"
                    >
                      必須
                    </div>
                    <div class="profile-edit-form__label subhead-bold require">
                      ニックネーム
                    </div>
                    <div class="profile-edit-form__text">
                      <input
                        id="profile_edit_form_nickname"
                        v-model="nickname"
                        name="profile_edit_form[nickname]"
                        class="profile-edit-form__nickname-input"
                        :class="{
                          field_with_errors: hasNicknameError,
                          'input-form--inputted': isNicknameActive,
                          'input-form': !isNicknameActive,
                          'input-form--inputting': isNicknameActive
                        }"
                        placeholder="例：ギフティ太郎（20文字以内）"
                        maxlength="20"
                        @focus="onNicknameFocus"
                        @blur="onNicknameBlur"
                      />
                    </div>
                    <div class="profile-edit-form__nickname-caption">
                      <p>※何度でも変更が可能です</p>
                      <p>※20文字以内</p>
                    </div>
                    <div class="profile-edit-form__nickname-box-list">
                      ※プロフィールを公開する場合は、第三者に閲覧されても問題ない名前をお使いください
                    </div>
                  </div>
                  <div
                    class="profile-edit-form__screen-name profile-edit-form__group"
                  >
                    <div class="profile-edit-form__label subhead-bold require">
                      giftee ID
                    </div>
                    <div class="profile-edit-form__input-message-description">
                      プロフィールを共有するときのURLを設定できます
                    </div>
                    <div class="profile-edit-form__screen-name-area">
                      <div class="profile-edit-form__screen-name-url body-bold">
                        https://giftee.com/u/{{ screenName }}
                      </div>
                      <div class="profile-edit-form__text">
                        <input
                          id="profile_edit_form_screen_name"
                          v-model="screenName"
                          name="profile_edit_form[screen_name]"
                          :class="{
                            field_with_errors: hasScreenNameError,
                            'input-form--inputted': isScreenNameActive,
                            'input-form': !isScreenNameActive,
                            'input-form--inputting': isScreenNameActive
                          }"
                          placeholder="例：giftee-taro_0810"
                          maxlength="20"
                          @focus="onScreenNameFocus"
                          @blur="onScreenNameBlur"
                        />
                        <input
                          id="profile_edit_form_current_screen_name"
                          name="profile_edit_form[current_screen_name]"
                          type="hidden"
                          :value="currentScreenName"
                        />
                      </div>
                      <div class="profile-edit-form__screen-name-caption">
                        <p>※小文字半角英数字と「_」「-」が利用可能</p>
                        <p>※20文字以内</p>
                      </div>
                    </div>
                    <div class="profile-edit-form__note-group">
                      <div class="minimal">
                        ※他のユーザーが設定したgiftee IDを選ぶことはできません
                        <br />
                        ※過去設定されたgiftee IDを再度選ぶことはできません
                      </div>
                    </div>
                  </div>
                  <div
                    class="profile-edit-form__birthday-content profile-edit-form__group"
                  >
                    <div class="profile-edit-form__birthday-checkbox-area">
                      <div class="profile-edit-form__label subhead-bold">
                        誕生日
                      </div>
                      <div class="profile-edit-form__disable-button body">
                        <label for="profile_edit_form_birthday_undisplayable">
                          <span
                            :class="{
                              'checkbox--checked': isBirthdayUndisplayable,
                              checkbox: !isBirthdayUndisplayable
                            }"
                          >
                            <input
                              id="profile_edit_form_birthday_undisplayable"
                              v-model="isBirthdayUndisplayable"
                              type="checkbox"
                              name="profile_edit_form[birthday_undisplayable]"
                            />
                            非公開にする
                          </span>
                        </label>
                      </div>
                      <input
                        type="hidden"
                        name="profile_edit_form[current_birthday]"
                        :value="currentBirthday"
                      />
                    </div>
                    <div class="profile-edit-form__input-message-description">
                      公開すると、あなたのプロフィールを閲覧した人が誕生日を知ることができます
                    </div>
                    <div
                      v-if="bithdayEditable"
                      class="profile-edit-form__birthday--editable"
                    >
                      <div class="profile-edit-form__birthday-selects">
                        <div class="profile-edit-form__birthday-select-pair">
                          <select
                            id="profile_edit_form_birth_year"
                            v-model="birthYear"
                            name="profile_edit_form[birth_year]"
                            class="profile-edit-form__birthday-select profile-edit-form__birthday-select--four-digit"
                            :class="{
                              'select-form--selected': isBirthYearSelected,
                              'select-form--unselected': !isBirthYearSelected,
                              field_with_errors: hasBirthYearError
                            }"
                          >
                            <option
                              v-for="index in 90"
                              :key="1900 + index"
                              :value="`${1900 + index}`"
                            >
                              {{ 1900 + index }}
                            </option>
                            <option value="" />
                            <option
                              v-for="index in currentYear - 1990"
                              :key="1990 + index"
                              :value="`${1990 + index}`"
                            >
                              {{ 1990 + index }}
                            </option>
                          </select>
                          <span class="profile-edit-form__birthday-select-unit">
                            年
                          </span>
                        </div>
                        <div class="profile-edit-form__birthday-select-pair">
                          <select
                            id="profile_edit_form_birth_month"
                            v-model="birthMonth"
                            name="profile_edit_form[birth_month]"
                            class="profile-edit-form__birthday-select profile-edit-form__birthday-select--two-digit select-form"
                            :class="{
                              'select-form--selected': isBirthMonthSelected,
                              'select-form--unselected': !isBirthMonthSelected,
                              field_with_errors: hasBirthMonthError
                            }"
                          >
                            <option value="" />
                            <option
                              v-for="index in 12"
                              :key="index"
                              :value="index.toString()"
                            >
                              {{ index }}
                            </option>
                          </select>
                          <span class="profile-edit-form__birthday-select-unit">
                            月
                          </span>
                        </div>
                        <div class="profile-edit-form__birthday-select-pair">
                          <select
                            id="profile_edit_form_birth_day"
                            v-model="birthDay"
                            name="profile_edit_form[birth_day]"
                            class="profile-edit-form__birthday-select profile-edit-form__birthday-select--two-digit"
                            :class="{
                              'select-form--selected': isBirthDaySelected,
                              'select-form--unselected': !isBirthDaySelected,
                              field_with_errors: hasBirthDayError
                            }"
                          >
                            <option value="" />
                            <option
                              v-for="index in 31"
                              :key="index"
                              :value="index.toString()"
                            >
                              {{ index }}
                            </option>
                          </select>
                          <span class="profile-edit-form__birthday-select-unit">
                            日
                          </span>
                        </div>
                      </div>
                      <div class="profile-edit-form__note-group minimal">
                        ※公開していても、生まれ年は表示されません
                        <br />
                        ※登録した誕生日は後から変更できません
                      </div>
                    </div>
                    <div
                      v-else
                      class="profile-edit-form__birthday--not-editable"
                    >
                      <div class="profile-edit-form__birth-date subhead">
                        {{ birthdayText }}
                        <input
                          type="hidden"
                          name="profile_edit_form[birth_year]"
                          :value="initialBirthYear"
                        />
                        <input
                          type="hidden"
                          name="profile_edit_form[birth_month]"
                          :value="initialBirthMonth"
                        />
                        <input
                          type="hidden"
                          name="profile_edit_form[birth_day]"
                          :value="initialBirthDay"
                        />
                      </div>
                      <div class="profile-edit-form__note-group">
                        <div class="minimal">
                          ※公開していても、生まれ年は表示されません
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    class="profile-edit-form__description-content profile-edit-form__group"
                  >
                    <div class="profile-edit-form__label subhead-bold">
                      自己紹介/コメント（任意）
                    </div>
                    <div class="profile-edit-form__textarea">
                      <textarea
                        id="profile_edit_form_description_content"
                        v-model="descriptionContent"
                        name="profile_edit_form[description_content]"
                        class="textarea--short profile-edit-form__description-input"
                        :class="{
                          field_with_errors: hasDescriptionError,
                          'input-form--inputted': isDescriptionActive,
                          'input-form': !isDescriptionActive,
                          'input-form--inputting': isDescriptionActive
                        }"
                        placeholder="例：趣味は映画鑑賞、甘いものが好きです。よろしくお願いします！"
                        maxlength="300"
                        cols="30"
                        rows="5"
                        @focus="onDescriptionFocus"
                        @blur="onDescriptionBlur"
                      />
                    </div>
                    <div class="profile-edit-form__input-message-caption">
                      ※300文字以内
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="profile-edit-section__gray-border" />
            <div class="profile-edit-section__foot">
              <div class="profile-edit-section__foot-content">
                <div class="profile-edit-form__submit-button">
                  <button
                    class="btn btn--primary"
                    :disabled="!isSubmittable"
                    @click.prevent="onClickPreviewButton"
                  >
                    保存してプレビュー
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </section>
    <TextModal
      v-if="isIconModalActive"
      title-text="プロフィール画像の削除"
      caption-text="本当に画像を削除しますか？
代わりにデフォルト画像が使われます。"
      primary-button-text="削除する"
      cancel-button-text="キャンセル"
      @clicked-modal-cover="closeIconModal"
      @clicked-primary="onClickIconModalPrimaryButton"
      @clicked-cancel="closeIconModal"
    />
    <TextModal
      v-if="isGifteeIdModalActive"
      title-text="giftee IDを変更しますか？"
      caption-text="IDを変更するとプロフィール画面のURLが変わり、あなたへのフォローが解除されますのでご注意ください。
※1日1回のみ変更できます"
      primary-button-text="変更する"
      cancel-button-text="キャンセル"
      @clicked-modal-cover="closeGifteeIdModal"
      @clicked-primary="onClickGifteeIdModalPrimaryButton"
      @clicked-cancel="closeGifteeIdModal"
    />
  </div>
</template>
<script>
import axios from 'axios'
import FeedbackError from '../../atoms/feedbacks/feedback-error.vue'
import FeedbackSuccess from '../../atoms/feedbacks/feedback-success.vue'
import PageHeadline from '../../molecules/headlines/PageHeadline.vue'
import AccountIconWh104 from '../../atoms/account_icons/AccountIconWh104.vue'
import CameraBlack from '../../atoms/svg_icons/CameraBlack.vue'
import TextModal from '../../molecules/modals/text-modal.vue'
export default {
  components: {
    FeedbackError,
    FeedbackSuccess,
    PageHeadline,
    AccountIconWh104,
    CameraBlack,
    TextModal
  },
  props: {
    isApp: {
      type: Boolean,
      required: true
    },
    postUrl: {
      type: String,
      required: true
    },
    authenticityToken: {
      type: String,
      required: true
    },
    errorMessages: {
      type: Array,
      required: false,
      default: () => []
    },
    initialTempImageFile: {
      type: String,
      required: false,
      default: ''
    },
    initialImageId: {
      type: String,
      required: false,
      default: ''
    },
    initialNickname: {
      type: String,
      required: false,
      default: ''
    },
    initialScreenName: {
      type: String,
      required: false,
      default: ''
    },
    currentScreenName: {
      type: String,
      required: false,
      default: ''
    },
    initialBirthdayUndisplayable: {
      type: String,
      required: false,
      default: ''
    },
    currentBirthday: {
      type: String,
      required: false,
      default: ''
    },
    bithdayEditable: {
      type: Boolean,
      required: false,
      default: false
    },
    initialBirthYear: {
      type: String,
      required: false,
      default: ''
    },
    initialBirthMonth: {
      type: String,
      required: false,
      default: ''
    },
    initialBirthDay: {
      type: String,
      required: false,
      default: ''
    },
    initialDescription: {
      type: String,
      required: false,
      default: ''
    },
    hasNicknameError: {
      type: Boolean,
      required: false,
      default: false
    },
    hasScreenNameError: {
      type: Boolean,
      required: false,
      default: false
    },
    hasBirthYearError: {
      type: Boolean,
      required: false,
      default: false
    },
    hasBirthMonthError: {
      type: Boolean,
      required: false,
      default: false
    },
    hasBirthDayError: {
      type: Boolean,
      required: false,
      default: false
    },
    hasDescriptionError: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data() {
    return {
      tempImageFile: '',
      imageId: '',
      nickname: '',
      screenName: '',
      birthYear: '',
      birthMonth: '',
      birthDay: '',
      descriptionContent: '',
      isFocusNickname: false,
      isFocusScreenName: false,
      isFocusDescription: false,
      isBirthdayUndisplayable: false,
      feedbackErrorMessage: '',
      hasErrors: false,
      feedbackSuccessMessage: '',
      hasFeedbackSuccess: false,
      currentImage: '',
      uploadedImage: '',
      isUploaded: false,
      fileErrorMessage: '',
      isIconModalActive: false,
      isGifteeIdModalActive: false,
      isSubmitting: false
    }
  },
  computed: {
    currentYear() {
      const date = new Date()
      return date.getFullYear()
    },
    birthdayText() {
      return `${this.initialBirthMonth}月${this.initialBirthDay}日`
    },
    isNotEmptyNickname() {
      return this.nickname.length !== 0
    },
    isNotEmptyScreenName() {
      return this.screenName.length !== 0
    },
    isNicknameActive() {
      return this.isNotEmptyNickname || this.isFocusNickname
    },
    isScreenNameActive() {
      return this.isNotEmptyScreenName || this.isFocusScreenName
    },
    isDescriptionActive() {
      return this.descriptionContent.length !== 0 || this.isFocusDescription
    },
    isBirthYearSelected() {
      return this.birthYear !== ''
    },
    isBirthMonthSelected() {
      return this.birthMonth !== ''
    },
    isBirthDaySelected() {
      return this.birthDay !== ''
    },
    isBirthdayValid() {
      return (
        (this.isBirthYearSelected &&
          this.isBirthMonthSelected &&
          this.isBirthDaySelected) ||
        (!this.isBirthYearSelected &&
          !this.isBirthMonthSelected &&
          !this.isBirthDaySelected)
      )
    },
    isSubmittable() {
      return (
        this.isNotEmptyNickname &&
        this.isNotEmptyScreenName &&
        this.isBirthdayValid &&
        !this.isSubmitting
      )
    },
    hasFileError() {
      return this.fileErrorMessage.length !== 0
    }
  },
  mounted() {
    if (this.errorMessages.length >= 1) {
      this.feedbackErrorMessage = 'プロフィール情報を登録できません'
      this.hasErrors = true
      this.enterError()
    }

    this.tempImageFile = this.initialTempImageFile
    this.imageId = this.initialImageId
    this.currentImage = this.tempImageFile || ''

    if (this.initialBirthdayUndisplayable === 'true') {
      this.isBirthdayUndisplayable = true
    }

    if (this.initialNickname) {
      this.nickname = this.initialNickname
      this.isFocusNickname = true
    }

    if (this.initialScreenName) {
      this.screenName = this.initialScreenName
      this.isFocusScreenName = true
    }

    if (this.initialBirthYear) {
      this.birthYear = this.initialBirthYear
    }

    if (this.initialBirthMonth) {
      this.birthMonth = this.initialBirthMonth
    }

    if (this.initialBirthDay) {
      this.birthDay = this.initialBirthDay
    }

    if (this.initialDescription) {
      this.descriptionContent = this.initialDescription
      this.isFocusDescription = true
    }
  },
  methods: {
    onNicknameFocus() {
      this.isFocusNickname = true
    },
    onNicknameBlur() {
      this.isFocusNickname = false
    },
    onScreenNameFocus() {
      this.isFocusScreenName = true
    },
    onScreenNameBlur() {
      this.isFocusScreenName = false
    },
    onDescriptionFocus() {
      this.isFocusDescription = true
    },
    onDescriptionBlur() {
      this.isFocusDescription = false
    },
    enterError() {
      setTimeout(() => {
        this.hasErrors = false
      }, 3000)
    },
    enterFeedbackSuccess() {
      setTimeout(() => {
        this.hasFeedbackSuccess = false
      }, 3000)
    },
    onFileChange(e) {
      this.fileErrorMessage = ''
      const files = e.target.files || e.dataTransfer.files
      if (
        this.validationFileSize(files[0].size) &&
        this.validationFileType(files[0].type)
      ) {
        this.isUploaded = true
        this.createImage(files[0])
      }
    },
    createImage(file) {
      const reader = new FileReader()
      reader.onload = e => {
        this.uploadedImage = e.target.result
      }
      reader.readAsDataURL(file)
    },
    validationFileSize(fileSize) {
      if (fileSize > 20 * 1024 * 1024) {
        this.isUploaded = false
        this.fileErrorMessage = 'ファイルサイズは20MB以下にしてください'
        const uploadedFile = document.querySelector('#file_upload')
        uploadedFile.value = ''
        return false
      }
      return true
    },
    validationFileType(fileType) {
      const allowedExtensions = [
        'image/jpg',
        'image/jpeg',
        'image/png',
        'image/gif'
      ]
      if (!allowedExtensions.includes(fileType)) {
        this.isUploaded = false
        this.fileErrorMessage = 'ファイル形式はJPG、PNG、GIFのみ可能です'
        return false
      }
      return true
    },
    closeIconModal() {
      this.isIconModalActive = false
    },
    closeGifteeIdModal() {
      this.isGifteeIdModalActive = false
    },
    onClickIconDelete() {
      this.isIconModalActive = true
    },
    async onClickIconModalPrimaryButton() {
      try {
        const res = await axios.delete(
          '/api/v1/users/@me/profiles/destroy_icon'
        )
        if (res.data.status === 200) {
          // フロント側の入力をリセット
          this.currentImage = ''
          this.uploadedImage = ''
          this.isUploaded = ''
          this.tempImageFile = ''
          this.imageId = ''
          document.querySelector('#file_upload').value = ''

          this.closeIconModal()
          this.feedbackSuccessMessage = 'プロフィール画像を削除しました'
          this.hasFeedbackSuccess = true
          this.enterFeedbackSuccess()
        }
      } catch (error) {
        this.closeIconModal()
        this.feedbackErrorMessage =
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'システムエラーが発生しました。しばらく経ってから再度アクセスしてください'
        this.hasErrors = true
        this.enterError()
      }
    },
    onClickGifteeIdModalPrimaryButton() {
      this.submitForm()
    },
    submitForm() {
      this.isSubmitting = true
      document.profile_edit_form.submit()
    },
    onClickPreviewButton() {
      if (
        this.currentScreenName !== '' &&
        this.currentScreenName !== this.screenName
      ) {
        this.isGifteeIdModalActive = true
      } else {
        this.submitForm()
      }
    }
  }
}
</script>
<style lang="scss" scoped>
#file_upload {
  display: none;
}

.profile-edit-container {
  .profile-edit-section {
    &__head {
      margin-bottom: 16px;
    }

    &__body {
      background-color: $white;
      overflow: hidden;
    }

    &__main {
      margin: 24px auto;
      padding: 0 16px;
      max-width: $small-contents-width;
      box-sizing: border-box;
    }

    &__foot {
      padding: 0 16px;
      margin: 40px auto;
    }

    &__foot-content {
      margin: auto;
      max-width: $small-contents-width;
    }

    &__gray-border {
      height: 1.6rem;
      background-color: $gray-3;
    }

    &__buttons {
      margin: 40px auto;

      max-width: 342px;
      width: 100%;
    }
  }

  .profile-edit-form {
    &__group {
      margin-top: 40px;
    }

    &__icon-field {
      margin: auto;
      width: 104px;
      position: relative;
    }

    &__account-icon {
      margin: auto;
    }

    &__icon-overlay {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      background-color: $gray-2;
      cursor: pointer;
      position: absolute;
      top: calc(104px - 32px); // icon-fieldの高さ - iconの高さ
      right: 0;

      > svg {
        display: block;
        margin: auto;
        height: 100%;
      }
    }

    &__icon-delete-button {
      @extend %hover-common;
      display: flex;
      font-size: 1.6rem;
      line-height: 1.6;
      color: $link;
      cursor: pointer;
      text-decoration: none;
      font-size: 1.4rem;
      margin: 16px auto 0;
    }

    &__profile-guide-link {
      @extend %module-text;
      color: $link;
      display: block;
      margin-top: 40px;
    }

    &__nickname-input {
      margin-top: 8px;
    }

    &__nickname-caption {
      @extend %caption;
      padding-top: 8px;
    }

    &__nickname-box-list {
      margin-top: 8px;
      font-size: 1rem;
      line-height: 1.6;
      padding: 8px;
      max-width: 100%;
      border-radius: 4px;
      color: $gray-1;
      background-color: $gray-3;
      border: 1px solid $gray-2;
    }

    &__description-input {
      margin-top: 8px;
    }

    &__input-content-area {
      text-align: left;
    }

    &__alert-box-area {
      margin-top: 40px;

      text-align: left;

      li {
        list-style: inside;
      }
    }

    &__note-group {
      padding: 8px;

      background-color: $gray-3;
      border: 1px solid $gray-2;
    }

    &__required_caution {
      color: $danger;
    }

    &__birthday-checkbox-area {
      display: flex;
      align-items: center;
    }

    &__birthday-selects {
      display: flex;
      margin: 16px 0;
    }

    &__birthday-select {
      margin: 0 4px 0 0;

      &--four-digit {
        width: 8rem;
      }

      &--two-digit {
        width: 6.4rem;
      }
    }

    &__birthday-select-pair {
      display: flex;
      &:not(:last-child) {
        margin-right: 16px;
      }
    }

    &__birthday-select-unit {
      @extend %caption;
      margin: auto;
      font-weight: normal;
      color: $black;
    }

    &__birth-date {
      margin: 16px 0;
    }

    &__disable-button {
      padding-left: 16px;

      font-weight: normal;
    }

    &__input-message-description {
      @extend %caption;
      margin: 8px 0;
    }

    &__input-message-caption {
      @extend %caption;
      margin-top: 8px;
    }

    &__screen-name-url {
      margin-bottom: 8px;
      color: $gray-1;
    }

    &__screen-name-area {
      margin-bottom: 8px;
    }

    &__screen-name-caption {
      @extend %caption;
      margin-bottom: 8px;
    }

    &__textarea {
      textarea.input-form {
        font-size: 1.4rem;
        // 文字サイズ(1.4rem) * line_height(1.6) * プレースホルダーの行数(2) + padding(12px) * 上下(2)
        height: calc(1.4rem * 1.6 * 2 + 12px * 2);
      }

      textarea.input-form--inputted,
      textarea.input-form--inputting {
        height: 8em;
      }
    }
  }
}
</style>
